import { useState } from 'react';
import { ErrorModal, Loader, useCurrentUser } from '@beholos/holoscommon';
import { StepOne } from './components/StepOne';
import { StepTwo } from './components/StepTwo';
import { StepThree } from './components/StepThree';
import { StepFour } from './components/StepFour';
import { ChevronLeft } from 'lucide-react';
import { acceptDiscount, cancel } from '../../services/payment.service';
import { usePageTitle } from '../../hooks/usePageTitle';

export default function Cancel() {
  usePageTitle('Cancelar Plan - Holos');
  
  const { user, loading, refetch } = useCurrentUser();
  const [reason, setReason] = useState({ type: '', comment: '' });
  const [acceptedDiscount, setAcceptedDiscount] = useState(false);
  const [likelyHoodOfReturning, setLikelyHoodOfReturning] = useState(0);
  const [step, setStep] = useState(0);
  const [processingStep, setProcessingStep] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const nextStep = async () => {
    if (step === 2) {
      setProcessingStep(true);
      try {
        if (acceptedDiscount) {
          await handleAcceptDiscount();
        } else {
          await handleCancel();
        }
      } finally {
        setProcessingStep(false);
      }
    }

    const { acceptedCancellationDiscount, isCurrentlyAFreeTrialUser } = user || {};
    setStep(prev => prev + 1);

    if (
      step === 1 &&
      (acceptedCancellationDiscount || isCurrentlyAFreeTrialUser || (user?.plan?.basePlan))
    ) {
      setAcceptedDiscount(false);
    }
  };

  const handleClose = () => {
    window.location.href = '/user/mi-membresia';
  };

  const handleCancel = async () => {
    const response = await cancel(reason, likelyHoodOfReturning || '');

    if (response?.status === 200) {
      refetch();
    } else {
      setError(response?.error || 'Algo salió mal');
    }
  };

  const handleAcceptDiscount = async () => {
    const response = await acceptDiscount();

    if (response?.status === 200) {
      refetch();
    } else {
      setError(response?.error || 'Algo salió mal');
    }
  };

  if (loading || !user) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader />
      </div>
    );
  }

  const plan = user?.plan;
  // const hasBasePlanDiscount = !!plan?.basePlan;
  // const { acceptedCancellationDiscount = false, isCurrentlyAFreeTrialUser = false } = user;
  const percentageComplete = step >= 2 ? 100 : 33.33 + step * 33.33;

  if (!plan) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader />
      </div>
    );
  }

  return (
    <div>
      {step <= 2 && (<div className="bg-holos px-4 md:px-8 py-4 border-b">
        <a
          href="/user/mi-membresia"
          className="text-black font-semibold hover:text-gray-700"
        >
          <ChevronLeft className="inline-block mr-1" />
          Volver al inicio
        </a>
      </div>)}

      <div className="max-w-lg mx-auto my-8 bg-white rounded-2xl border border-gray-200 shadow-md p-6 my-6">
        <div className="px-4 py-3">
          <div className="flex justify-between mb-2">
            <div className="text-sm font-bold">
              Paso {step >= 2 ? 3 : step + 1} de 3
            </div>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-holos-dark h-2 rounded-full transition-all duration-300"
              style={{ width: `${percentageComplete}%` }}
            />
          </div>
        </div>

        <div className="p-4 md:p-8">
          {step === 0 && (
            <StepOne
              nextStep={nextStep}
              reason={reason.type}
              comment={reason.comment}
              setReason={(value) => setReason(prev => ({ ...prev, type: value }))}
              setComment={(event) => setReason(prev => ({ ...prev, comment: event.target.value }))}
            />
          )}

          {step === 1 && (
            <StepTwo
              nextStep={nextStep}
              likelyHoodOfReturning={likelyHoodOfReturning}
              setLikelyHoodOfReturning={setLikelyHoodOfReturning}
            />
          )}

          {step === 2 && (
            <StepThree
              // disableCancellationDiscount={acceptedCancellationDiscount || isCurrentlyAFreeTrialUser || hasBasePlanDiscount}
              disableCancellationDiscount={true}
              nextStep={nextStep}
              toggleDiscount={() => setAcceptedDiscount(prev => !prev)}
              loading={processingStep}
              acceptedDiscount={acceptedDiscount}
              planName={plan.name}
              cycleDuration={plan.cycleDuration}
              numberOfPasses={plan.numberOfPasses}
              discountedPrice={plan.price ? plan.price * 0.85 : 0}
              discountedCurrency={plan.currencySymbol}
            />
          )}

          {step === 3 && (
            <StepFour
              nextStep={handleClose}
              acceptedDiscount={acceptedDiscount}
              discountedPrice={plan.price ? plan.price * 0.85 : 0}
              cycleDuration={plan.cycleDuration}
              discountedCurrency={plan.currencySymbol}
            />
          )}
        </div>

        <ErrorModal isOpen={!!error} message={error || ''} onClose={() => setError(null)} />
      </div>
    </div>
  );
}